import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Plus, X, Map, MessageCircle } from "lucide-react";

/** Pages where the FAB would overlap important content */
const HIDDEN_PATHS = ["/create-trip", "/messages"];

export function FloatingActionButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const shouldHide = HIDDEN_PATHS.some((p) => location.startsWith(p));

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    },
    [isOpen],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // Close when navigating
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const actions = useMemo(() => [
    { label: t("fab.planTrip", "Plan Trip"), icon: Map, path: "/create-trip" },
    { label: t("fab.messages", "Messages"), icon: MessageCircle, path: "/messages" },
  ], [t]);

  if (shouldHide) return null;

  return (
    <div
      ref={containerRef}
      className="fixed bottom-20 right-4 z-30 flex flex-col items-end gap-3 sm:hidden"
    >
      {/* Speed-dial actions */}
      {isOpen &&
        actions.map((action) => (
          <button
            key={action.path}
            onClick={() => setLocation(action.path)}
            className="flex items-center gap-2 rounded-full pl-3 pr-4 py-2 text-white shadow-lg transition-all duration-200 animate-in fade-in slide-in-from-bottom-2"
            style={{
              background: "var(--fab-bg)",
              boxShadow: "var(--fab-glow)",
            }}
            aria-label={action.label}
          >
            <action.icon className="h-5 w-5" aria-hidden="true" />
            <span className="text-sm font-medium">{action.label}</span>
          </button>
        ))}

      {/* Main FAB — coral gradient with glow */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex h-14 w-14 items-center justify-center rounded-full text-white transition-transform duration-200 hover:scale-105 active:scale-95"
        style={{
          background: "var(--fab-bg)",
          boxShadow: "var(--fab-glow)",
        }}
        aria-label={t("fab.toggle", "Quick actions")}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className="h-6 w-6" aria-hidden="true" />
        ) : (
          <Plus className="h-6 w-6" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
